import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { IAssistantMessage, IQuestionOption } from '@/features/assistant/types';
import { useGetDBRAnswer } from '@/features/assistant/hooks';
import { deepBottomRailApi, getTaskStatusApi, fetchResultAsBlobUrl } from '../api';
import { urlToFile, buildResultUrl } from '../utils/imageHelpers';
import { IDeepBottomRailRequest, ITaskStatus } from '../types';

const POLL_INTERVAL_MS = 1000;
const DEBOUNCE_MS = 300;

interface DeepBottomRailState {
  isApplying: boolean;
  dbrImageUrl: string | null;
  lastTaskStatus: ITaskStatus | null;
  error: string | null;
}

/**
 * Polls a deep-bottom-rail task until SUCCESS or FAILURE.
 * Returns the full result URL on success.
 */
const pollUntilDone = async (
  taskId: string,
  isCancelled: () => boolean,
): Promise<string> => {
  while (true) {
    if (isCancelled()) throw new Error('cancelled');

    const status = await getTaskStatusApi(taskId);

    if (status.state === 'SUCCESS') {
      if (!status.result_url) throw new Error('Task succeeded but result_url is missing');
      return buildResultUrl(status.result_url);
    }

    if (status.state === 'FAILURE') {
      throw new Error(status.error || `Task ${taskId} failed`);
    }

    // PENDING / PROCESSING — wait then retry
    await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
  }
};

/**
 * Deep bottom rail blending hook.
 *
 * - Only fires when the DBR question is answered "yes" (isDbrEnabled).
 * - Debounces 700ms so rapid changes don't fire multiple calls.
 * - Calls /api/deep-bottom-rail-auto — frame detection is automatic, no
 *   per-product rails JSON needs to be supplied anymore.
 *
 * @param messages       - Current assistant messages
 * @param windowImageUrl - The window template image URL (selectedProduct?.thumbnail)
 * @param selectedProduct - The selected product option
 * @param enabled        - When false, seeds the ref so no call fires until a real change occurs
 * @param forceKey - Lets a caller force a genuine re-run (e.g. when grill
 *                   resolves to "None" and the de-grilled base needs to be
 *                   freshly verified) — bumping it changes dbrKey, which is
 *                   the only thing the effect below actually keys its "did
 *                   anything change" check on, so it's a real re-trigger
 *                   rather than just clearing output state.
 */
export const useDeepBottomRail = (
  messages: IAssistantMessage[],
  windowImageUrl: string | null,
  selectedProduct?: IQuestionOption,
  enabled: boolean = true,
  forceKey?: string | number,
) => {
  const [state, setState] = useState<DeepBottomRailState>({
    isApplying: false,
    dbrImageUrl: null,
    lastTaskStatus: null,
    error: null,
  });

  const { isDbrEnabled } = useGetDBRAnswer({ messages });
  // Key changes when isDbrEnabled, the window image, or the selected product
  // changes — including the product identity (not just its thumbnail URL) so
  // switching to a different product that happens to share the same base
  // thumbnail image still forces a fresh re-blend rather than reusing a
  // cached result keyed only on the URL.
  const dbrKey = useMemo(() => {
    if (!isDbrEnabled || !windowImageUrl) return "";
    return `dbr:${windowImageUrl}:${selectedProduct?.optionId ?? ''}:${forceKey ?? ''}`;
  }, [isDbrEnabled, windowImageUrl, selectedProduct?.optionId, forceKey]);

  const lastAppliedRef = useRef({ dbrKey: '' });
  const currentBlobUrlRef = useRef<string | null>(null);
  const prevEnabledRef = useRef(false);

  const deepBottomRailMutation = useMutation({
    mutationFn: (data: IDeepBottomRailRequest) => deepBottomRailApi(data),
  });

  // Keyed on the stable optionId (primitive), NOT the selectedProduct object
  // reference. Callers (e.g. ConfigurationDrawerContext's openDrawer) rebuild
  // a fresh product object on every drawer open even for the SAME underlying
  // product — keying on the object reference made this effect (wrongly) treat
  // every re-open as a product change, reset, and re-run DBR, producing a new
  // blob: URL (unique per run even for identical image bytes) that then forced
  // colorize and grill to cascade-rerun too, even though nothing had actually
  // changed.
  const selectedProductId = selectedProduct?.optionId;
  useEffect(() => {
    if (selectedProductId) {
      setState({
        isApplying: false,
        dbrImageUrl: null,
        lastTaskStatus: null,
        error: null,
      });
      lastAppliedRef.current = { dbrKey: "" };
      prevEnabledRef.current = false;
    }
  }, [selectedProductId]);

  useEffect(() => {
    // Not enabled — seed the ref so no call fires until a real change occurs
    if (!enabled) {
      lastAppliedRef.current = { dbrKey };
      prevEnabledRef.current = false;
      return;
    }

    // enabled just transitioned false→true: clear the ref so the full pipeline
    // re-runs even if the key hasn't changed (e.g. color-only change with existing DBR)
    const justEnabled = !prevEnabledRef.current;
    prevEnabledRef.current = true;

    // Nothing changed — skip (but not on the first enable transition)
    if (!justEnabled && lastAppliedRef.current.dbrKey === dbrKey) return;

    // DBR not enabled (e.g. answer switched to "Without Deep Bottom Rail") or
    // no window image — clear previous result. Must also clear lastTaskStatus,
    // not just dbrImageUrl: downstream consumers (AssistantContext,
    // ConfigurationDrawerContext) read dbrBlend.lastTaskStatus?.result_url as
    // the primary DBR output, so leaving it stale here would keep feeding the
    // old DBR-blended image into colorize/grill/the final AI blend even after
    // DBR was turned off.
    if (!dbrKey || !isDbrEnabled || !windowImageUrl) {
      lastAppliedRef.current = { dbrKey };
      setState((prev) => ({
        ...prev,
        dbrImageUrl: null,
        lastTaskStatus: null,
        error: null,
      }));
      return;
    }

    let cancelled = false;

    // Debounce: only fire after 700ms of inactivity
    const timer = setTimeout(async () => {
      setState((prev) => ({ ...prev, isApplying: true, error: null }));


      try {
        const windowFile = await urlToFile(windowImageUrl, "window.png");

        if (!windowFile || windowFile.size === 0) {
          setState((prev) => ({ ...prev, isApplying: false }));
          return;
        }

        if (cancelled) {
          return;
        }

        const submission = await deepBottomRailMutation.mutateAsync({
          window_image: windowFile,
        });

        setState((prev) => ({ ...prev, lastTaskStatus: null }));

        const resultUrl = await pollUntilDone(
          submission.task_id,
          () => cancelled,
        );

        if (!cancelled) {
          const blobUrl = await fetchResultAsBlobUrl(resultUrl);

          if (!cancelled) {
            if (currentBlobUrlRef.current?.startsWith("blob:")) {
              URL.revokeObjectURL(currentBlobUrlRef.current);
            }
            currentBlobUrlRef.current = blobUrl;


            lastAppliedRef.current = { dbrKey };
            setState((prev) => ({
              ...prev,
              isApplying: false,
              dbrImageUrl: blobUrl,
              lastTaskStatus: {
                task_id: submission.task_id,
                state: "SUCCESS",
                result_url: resultUrl,
              },
            }));
          }
        }
      } catch (error) {
        if (!cancelled) {
          const msg = error instanceof Error ? error.message : String(error);
          if (msg !== "cancelled") {
            setState((prev) => ({ ...prev, isApplying: false, error: msg }));
          }
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled, dbrKey, isDbrEnabled, windowImageUrl]);

  const reset = useCallback(() => {
    if (currentBlobUrlRef.current?.startsWith('blob:')) {
      URL.revokeObjectURL(currentBlobUrlRef.current);
      currentBlobUrlRef.current = null;
    }
    setState({ isApplying: false, dbrImageUrl: null, lastTaskStatus: null, error: null });
    lastAppliedRef.current = { dbrKey: '' };
    prevEnabledRef.current = false;
  }, []);

  return {
    dbrImageUrl: state.dbrImageUrl,
    isApplying: state.isApplying,
    lastTaskStatus: state.lastTaskStatus,
    error: state.error,
    isDbrEnabled,
    reset,
  };
};
